import { ModuleManager } from "./ModuleManager";
import { StringData } from "../StringData";
import { StringMirrorObject } from "../../objects/StringMirrorObject";
import { StringObject } from "../../objects/StringObject";
import { EventManager } from "./EventManager";
import { ISettingsChangeData } from "../../models/event/ISettingsChangeData";
import { StringToolsContainer } from "../StringToolsContainer";

export class ObjectManager {
  private objects = new Map<string, StringObject>();
  private connectQueue: { id: string; element: HTMLElement }[] = [];
  private mirrors = new Map<string, StringMirrorObject>();
  private mirrorId = 1;
  private globalId = 1;

  constructor(
    private data: StringData,
    private modules: ModuleManager,
    private events: EventManager,
    private tools: StringToolsContainer
  ) {}

  /**
   * Returns the object map (read-only).
   */
  get all(): ReadonlyMap<string, StringObject> {
    return this.objects;
  }

  /**
   * Adds a new object from an element.
   */
  public add(el: HTMLElement) {
    let idAttr = `string-${this.globalId++}`;
    let key = "string-id";

    if (el.getAttribute("string-id")) {
      idAttr = el.getAttribute("string-id")!;
      key = "string-id";
    }
    if (el.getAttribute("data-string-id")) {
      idAttr = el.getAttribute("data-string-id")!;
      key = "data-string-id";
    }

    const object =
      idAttr && this.objects.has(idAttr) ? this.objects.get(idAttr)! : new StringObject(idAttr, el);

    el.setAttribute(key, object.id);

    const keysAttr = el.getAttribute("string") ?? el.getAttribute("data-string");

    if (keysAttr) {
      object.keys = (keysAttr ?? "")
        .split("|")
        .map((s) => s.trim())
        .filter(Boolean);
    }

    el.setAttribute("string-inited", "");
    this.objects.set(object.id, object);

    const attributes = this.getAllAttributes(el);

    // Delegate core setup (dimensions, offsets, key, start/end, etc.)
    this.modules.core.forEach((m) => {
      if ("setupCoreProperties" in m && typeof m["setupCoreProperties"] === "function") {
        (m as any).setupCoreProperties(object, el, attributes);
      }
    });

    // Try connecting to modules
    this.modules.core.forEach((m) => {
      if (m.canConnect(object)) {
        m.initializeObject(this.globalId, object, el, attributes);
        m.calculatePositions(object, this.data.viewport.windowHeight);
        m.connectObject(object);
        m.addObject(object.id, object);
      }
    });

    // Restore connect-from
    const queueItems = this.connectQueue.filter((q) => q.id === object.id);
    queueItems.forEach((item) => this.attachMirrorToObject(object, item.element));
    this.connectQueue = this.connectQueue.filter((q) => q.id !== object.id);

    // Set up observers
    this.initObservers(object, el);

    this.checkInviewForObject(object);
  }

  /**
   * Removes an object by its id.
   */
  public remove(id: string) {
    const obj = this.objects.get(id);
    if (!obj) return;

    obj.events.clearAll();
    obj.getProperty<IntersectionObserver>("observer-progress")?.disconnect();
    obj.getProperty<IntersectionObserver>("observer-inview")?.disconnect();

    obj.htmlElement.removeAttribute("string-inited");
    obj.leave();
    obj.remove();

    obj.mirrorObjects.forEach((mirror) => {
      mirror.htmlElement.removeAttribute("string-mirror-id");
      this.mirrors.delete(mirror.id);

      const copyFromId =
        mirror.htmlElement.getAttribute("string-copy-from") ??
        mirror.htmlElement.getAttribute("data-string-copy-from");
      if (copyFromId) {
        this.enqueueConnection(copyFromId, mirror.htmlElement);
      }
    });

    this.objects.delete(id);
  }

  /**
   * Add an element that will connect later.
   */
  public enqueueConnection(id: string, element: HTMLElement) {
    if (this.connectQueue.some((item) => item.id === id && item.element === element)) {
      return;
    }
    this.connectQueue.push({ id, element });
  }

  public linkMirror(id: string, element: HTMLElement): void {
    const parent = this.objects.get(id);
    if (parent) {
      this.attachMirrorToObject(parent, element);
    } else {
      this.enqueueConnection(id, element);
    }
  }

  private attachMirrorToObject(object: StringObject, element: HTMLElement): StringMirrorObject {
    const existingId =
      element.getAttribute("string-mirror-id") ?? element.getAttribute("data-string-mirror-id");

    if (existingId) {
      const existing = this.mirrors.get(existingId);
      if (existing) {
        if (existing.parentObject === object) {
          return existing;
        }
        existing.parentObject.removeMirror(existingId);
        this.mirrors.delete(existingId);
      }
    }

    const mirrorId = existingId ?? `string-mirror-${this.mirrorId++}`;
    const mirror = new StringMirrorObject(mirrorId, element, object);
    element.setAttribute("string-mirror-id", mirrorId);
    object.addMirror(mirror);
    this.mirrors.set(mirrorId, mirror);

    const easingAttr =
      element.getAttribute("string-easing") ?? element.getAttribute("data-string-easing");
    if (easingAttr && easingAttr.trim().length > 0) {
      mirror.setEasing(this.tools.easingFunction.process({ easing: easingAttr }));
      mirror.setProperty("easing", easingAttr);
    }
    return mirror;
  }

  private detachMirrorByElement(element: HTMLElement): void {
    const mirrorId =
      element.getAttribute("string-mirror-id") ?? element.getAttribute("data-string-mirror-id");
    if (!mirrorId) return;
    this.detachMirrorById(mirrorId);
    element.removeAttribute("string-mirror-id");
  }

  private detachMirrorById(mirrorId: string): void {
    const mirror = this.mirrors.get(mirrorId);
    if (!mirror) return;
    mirror.parentObject.removeMirror(mirrorId);
    this.mirrors.delete(mirrorId);
  }

  /**
   * Retrieves all attributes of a given HTML element and returns them as a record.
   *
   * @param el - The HTML element from which to extract attributes.
   * @returns A record where the keys are attribute names and the values are attribute values.
   */
  private getAllAttributes(el: HTMLElement): Record<string, any> {
    const attributes: Record<string, any> = {};
    Array.from(el.attributes).forEach((attr) => {
      attributes[attr.name] = attr.value;
    });
    return attributes;
  }

  /**
   * Initializes IntersectionObservers for a given object and its associated HTML element.
   *
   * This method sets up observer:
   * - A "progress" observer to track when the object enters or leaves the viewport.
   *
   * The observers are configured with custom root margins and thresholds based on the object's properties.
   * Existing observers, if any, are disconnected before creating new ones.
   *
   * @param obj - The `StringObject` instance containing properties and methods for interaction.
   * @param el - The `HTMLElement` to observe for intersection changes.
   */
  private initObservers(obj: StringObject, el: HTMLElement) {
    const start = obj.getProperty<number>("offset-top") ?? 0;
    const end = obj.getProperty<number>("offset-bottom") ?? 0;
    obj.getProperty<IntersectionObserver>("observer-progress")?.disconnect();
    const progressCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((e) => {
        this.events.emit(`object:activate:${obj.id}`, e.isIntersecting);
        e.isIntersecting ? obj.enter() : obj.leave();
      });
    };
    const outsideProp = obj.getProperty<boolean>("outside-container");
    const outsideAttrValue =
      el.getAttribute("string-outside-container") ??
      el.getAttribute("data-string-outside-container");
    const outsideAttrNormalized =
      outsideAttrValue != null ? outsideAttrValue.trim().toLowerCase() : null;
    const outsideAttrFlag =
      outsideAttrNormalized === "" ||
      outsideAttrNormalized === "true" ||
      outsideAttrNormalized === "1";
    const isOutsideContainer = outsideProp != null ? outsideProp === true : outsideAttrFlag;
    const observerRoot =
      this.data.scroll.container === document.body || isOutsideContainer
        ? null
        : this.data.scroll.container;

    const progressObserver = new IntersectionObserver(progressCallback, {
      root: observerRoot,
      rootMargin: `${end + this.data.viewport.windowHeight}px 0px ${
        start + this.data.viewport.windowHeight
      }px 0px`,
      threshold: 0,
    });
    progressObserver.observe(el);
    obj.setProperty("observer-progress", progressObserver);
  }

  /**
   * Observes DOM mutations to auto-add/remove elements with [string] attribute.
   * Should be called once after DOM is ready.
   */
  public observeDOM(): void {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          // Removed elements
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;

            const element = node as HTMLElement;

            this.detachMirrorByElement(element);

            if (this.isFixed(element)) return;

            if (element.hasAttribute("string")) {
              this.handleRemoved(element);
            }

            element.querySelectorAll("[string],[data-string]").forEach((child) => {
              if (this.isFixed(child as HTMLElement)) return;
              this.handleRemoved(child as HTMLElement);
            });

            element
              .querySelectorAll("[string-copy-from],[data-string-copy-from]")
              .forEach((child) => this.detachMirrorByElement(child as HTMLElement));
          });

          // Added elements
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return;

            const element = node as HTMLElement;

            if (this.isFixed(element)) return;

            if (element.hasAttribute("string") && !element.hasAttribute("string-inited")) {
              this.add(element);
            }

            element
              .querySelectorAll("[string]:not([string-inited]),[data-string]:not([string-inited])")
              .forEach((child) => this.add(child as HTMLElement));

            // Check for connect-from logic
            const copyFrom =
              element.getAttribute("string-copy-from") ??
              element.getAttribute("data-string-copy-from");

            if (copyFrom) {
              this.linkMirror(copyFrom, element);
            }

            element
              .querySelectorAll("[string-copy-from],[data-string-copy-from]")
              .forEach((child) => {
                const childCopyFrom =
                  child.getAttribute("string-copy-from") ??
                  child.getAttribute("data-string-copy-from");
                if (childCopyFrom) {
                  this.linkMirror(childCopyFrom, child as HTMLElement);
                }
              });
          });

          // Let modules know about DOM rebuild
          this.modules.all.forEach((m) => m.onDOMRebuild());
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Removes an object and its observers.
   */
  private handleRemoved(el: HTMLElement): void {
    const id = el.getAttribute("string-id") ?? el.getAttribute("data-string-id");
    if (!id) return;

    const copyFrom =
      el.getAttribute("string-copy-from") ?? el.getAttribute("data-string-copy-from");
    if (copyFrom) {
      this.connectQueue = this.connectQueue.filter((q) => q.id !== copyFrom);
    }

    this.remove(id);
  }

  /**
   * Re-applies module initialization logic to all managed objects after settings change.
   *
   * This method should be called when `StringSettings` are updated at runtime,
   * especially if the new settings affect how modules calculate offsets,
   * easing, origins, or custom configuration.
   *
   * Internally, it re-runs `initializeObject`, `calculatePositions`, and `connectObject`
   * for each core module that can connect to the object.
   *
   * This is useful for supporting dynamic configuration updates without requiring
   * a full DOM rebuild or reinitialization.
   */
  public onSettingsChange(data: ISettingsChangeData) {
    this.objects.forEach((object) => {
      this.modules.core.forEach((m) => {
        let isCanRebuild = false;
        if (data.isDesktop) {
          if (m.permissions.desktop.rebuild.scrollHeight && data.scrollHeightChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.desktop.rebuild.width && data.widthChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.desktop.rebuild.height && data.heightChanged) {
            isCanRebuild = true;
          }
        } else {
          if (m.permissions.mobile.rebuild.scrollHeight && data.scrollHeightChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.mobile.rebuild.width && data.widthChanged) {
            isCanRebuild = true;
          }
          if (m.permissions.mobile.rebuild.height && data.heightChanged) {
            isCanRebuild = true;
          }
        }

        if (isCanRebuild || data.isForceRebuild) {
          if (m.canConnect(object)) {
            const attributes = this.getAllAttributes(object.htmlElement);
            m.initializeObject(this.globalId, object, object.htmlElement, attributes);
            m.calculatePositions(object, this.data.viewport.windowHeight);
            m.connectObject(object);
          }
        }
      });
    });
  }

  /**
   * Checks whether the element is marked as fixed (not managed).
   */
  private isFixed(el: HTMLElement): boolean {
    return el.hasAttribute("string-fixed");
  }

  public checkInview() {
    this.objects.forEach((object) => {
      this.checkInviewForObject(object);
    });
  }

  private checkInviewForObject(object: StringObject) {
    const scrollPos = this.data.scroll.transformedCurrent;
    const inviewStart = object.getProperty<number>("inview-start-position");
    const inviewEnd = object.getProperty<number>("inview-end-position");
    const wasInView = object.getProperty<boolean>("is-inview") ?? false;

    const start = Math.min(inviewStart, inviewEnd);
    const end = Math.max(inviewStart, inviewEnd);
    const isNowInView = scrollPos >= start && scrollPos <= end;

    let direction: "enter-top" | "enter-bottom" | "exit-top" | "exit-bottom" | null = null;
    if (!wasInView && isNowInView) {
      const distToStart = Math.abs(scrollPos - start);
      const distToEnd = Math.abs(end - scrollPos);
      direction = distToStart <= distToEnd ? "enter-top" : "enter-bottom";
    } else if (wasInView && !isNowInView) {
      direction = scrollPos < start ? "exit-top" : "exit-bottom";
    }
    if (isNowInView !== wasInView) {
      object.setProperty("is-inview", isNowInView);
      this.events.emit(`object:inview:${object.id}`, {
        inView: isNowInView,
        direction,
      });
      isNowInView ? object.show() : object.hide();
    }
  }
}
